import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";

import useApi from "../../hooks/useApi";
import Page from "./Page";
import Loading from "../ui/Loading";
import UserSearch from "../ui/UserSearch";
import AutoSearch from "../elements/AutoSearch";
import AlertDialog from "../ui/AlertDialog";

const EditMessage = () => {
  const navigate = useNavigate();
  const { messageId } = useParams();
  const { postData } = useApi();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    severity: "info",
    onConfirm: null,
  });
  const [formData, setFormData] = useState({
    title: "",
    text: "",
    text_fr: "",
    images: [],
    images_fr: [],
    priority: "normal",
    category: "",
    requiresAcknowledgement: false,
    allowReplies: true,
    expiresAt: null,
    // Targeting
    userId: null,
    clientId: null,
    projectId: null,
    province: "",
    bannerId: null,
    retailerId: null,
  });

  const isReadOnly = status !== "pending";

  useEffect(() => {
    if (messageId) {
      loadMessage();
    }
  }, [messageId]);

  const loadMessage = async () => {
    setLoading(true);
    const response = await postData({
      entityName: "Message",
      action: "getMessageThread",
      data: { messageId },
    });

    if (response.isSuccessful && Array.isArray(response.data)) {
      const rootMessage =
        response.data.find((m) => !m.parentMessageId) || response.data[0];

      if (rootMessage) {
        setStatus(rootMessage.status || "pending");
        setFormData({
          title: rootMessage.title || "",
          text: rootMessage.text || "",
          text_fr: rootMessage.text_fr || "",
          images: rootMessage.images || [],
          images_fr: rootMessage.images_fr || [],
          priority: rootMessage.priority || "normal",
          category: rootMessage.category || "",
          requiresAcknowledgement: !!rootMessage.requiresAcknowledgement,
          allowReplies: rootMessage.allowReplies !== false,
          expiresAt: rootMessage.expiresAt
            ? new Date(rootMessage.expiresAt)
            : null,
          userId: rootMessage.userId || null,
          clientId: rootMessage.clientId || null,
          projectId: rootMessage.projectId || null,
          province: rootMessage.province || "",
          bannerId: rootMessage.bannerId || null,
          retailerId: rootMessage.retailerId || null,
        });
      }
    }

    setLoading(false);
  };

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = async (event, language) => {
    if (isReadOnly) return;
    const files = Array.from(event.target.files);
    const field = language === "fr" ? "images_fr" : "images";

    const base64Images = await Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );

    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ...base64Images],
    }));
  };

  const handleRemoveImage = (language, index) => {
    if (isReadOnly) return;
    const field = language === "fr" ? "images_fr" : "images";
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const provinces = [
    "AB",
    "BC",
    "MB",
    "NB",
    "NL",
    "NS",
    "NT",
    "NU",
    "ON",
    "PE",
    "QC",
    "SK",
    "YT",
  ];

  const handleSave = async () => {
    if (!formData.title || !formData.text) {
      setDialog({
        open: true,
        title: "Validation Error",
        message: "Title and message text are required",
        severity: "warning",
        onConfirm: null,
      });
      return;
    }

    setLoading(true);
    const response = await postData({
      entityName: "Message",
      action: "updateMessage",
      data: {
        messageId,
        ...formData,
      },
    });

    if (response.isSuccessful) {
      navigate("/messages");
    } else {
      setDialog({
        open: true,
        title: "Error",
        message: response.message || "Failed to update message",
        severity: "error",
        onConfirm: null,
      });
    }
    setLoading(false);
  };

  const handleCancel = () => {
    navigate("/messages");
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, open: false });
  };

  if (loading) {
    return (
      <Page
        pageName="Edit Message"
        backPath="/messages"
      >
        <Loading />
      </Page>
    );
  }

  return (
    <>
      <AlertDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        severity={dialog.severity}
        onClose={handleDialogClose}
        onConfirm={dialog.onConfirm}
      />
      <Page
        pageName="Edit Message"
        backPath="/messages"
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Paper sx={{ p: 1 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column" }}
              gap={2}
            >
              {/* Line 1: Title, Category, Priority, Flags, Expires */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box sx={{ flex: "1 1 30%" }}>
                  <TextField
                    fullWidth
                    label="Title"
                    required
                    size="small"
                    value={formData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    disabled={isReadOnly}
                  />
                </Box>
                <Box sx={{ flex: "1 1 12%" }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Category"
                    value={formData.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                    disabled={isReadOnly}
                  />
                </Box>
                <Box sx={{ flex: "1 1 12%" }}>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={isReadOnly}
                  >
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      label="Priority"
                      onChange={(e) => handleChange("priority", e.target.value)}
                      disabled={isReadOnly}
                    >
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: "1 1 15%" }}>
                  <DatePicker
                    label="Expires At"
                    value={formData.expiresAt}
                    onChange={(newValue) => handleChange("expiresAt", newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                    disabled={isReadOnly}
                  />
                </Box>
                <Box
                  sx={{
                    flex: "1 1 25%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.requiresAcknowledgement}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          handleChange(
                            "requiresAcknowledgement",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Acknowledgement"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.allowReplies}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          handleChange("allowReplies", e.target.checked)
                        }
                      />
                    }
                    label="Allow Replies"
                  />
                </Box>
              </Box>

              {/* Line 2: Targeting Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 1 }}
                >
                  Targeting
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Note: If you select a specific user, all other filters will be
                  ignored.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 30%" }}>
                    <UserSearch
                      value={formData.userId}
                      onChange={(value) => handleChange("userId", value)}
                      label="Specific User"
                      size="small"
                      disabled={isReadOnly}
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 30%" }}>
                    <AutoSearch
                      value={formData.clientId}
                      onChange={(value) => handleChange("clientId", value)}
                      label="Client"
                      entityName="Client"
                      action="searchClient"
                      disabled={isReadOnly || !!formData.userId}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 30%" }}>
                    <AutoSearch
                      value={formData.projectId}
                      onChange={(value) => handleChange("projectId", value)}
                      label="Project"
                      entityName="Project"
                      action="searchProject"
                      disabled={isReadOnly || !!formData.userId}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 30%" }}>
                    <FormControl
                      fullWidth
                      disabled={isReadOnly || !!formData.userId}
                    >
                      <InputLabel>Province</InputLabel>
                      <Select
                        value={formData.province}
                        label="Province"
                        size="small"
                        onChange={(e) =>
                          handleChange("province", e.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>All Provinces</em>
                        </MenuItem>
                        {provinces.map((prov) => (
                          <MenuItem
                            key={prov}
                            value={prov}
                          >
                            {prov}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: "1 1 30%" }}>
                    <AutoSearch
                      value={formData.bannerId}
                      onChange={(value) => handleChange("bannerId", value)}
                      label="Banner"
                      entityName="Banner"
                      action="searchBanner"
                      disabled={isReadOnly || !!formData.userId}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 30%" }}>
                    <AutoSearch
                      value={formData.retailerId}
                      onChange={(value) => handleChange("retailerId", value)}
                      label="Retailer"
                      entityName="Retailer"
                      action="searchRetailer"
                      disabled={isReadOnly || !!formData.userId}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Line 3: English Section */}
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  English
                </Typography>
                <TextField
                  fullWidth
                  label="Message Text"
                  required
                  multiline
                  rows={6}
                  value={formData.text}
                  onChange={(e) => handleChange("text", e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={isReadOnly}
                />
                <Typography
                  variant="subtitle2"
                  gutterBottom
                >
                  Images
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={isReadOnly}
                >
                  Upload Images
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "en")}
                    disabled={isReadOnly}
                  />
                </Button>
                {formData.images.length > 0 && (
                  <Box
                    sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {formData.images.map((img, index) => (
                      <Box
                        key={index}
                        sx={{ position: "relative" }}
                      >
                        <img
                          src={img}
                          alt={`Upload ${index + 1}`}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                        <Button
                          size="small"
                          color="error"
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            minWidth: "auto",
                            p: 0.5,
                          }}
                          onClick={() => handleRemoveImage("en", index)}
                          disabled={isReadOnly}
                        >
                          ✕
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Line 3: French Section */}
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  French
                </Typography>
                <TextField
                  fullWidth
                  label="Message Text (French)"
                  multiline
                  rows={6}
                  value={formData.text_fr}
                  onChange={(e) => handleChange("text_fr", e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={isReadOnly}
                />
                <Typography
                  variant="subtitle2"
                  gutterBottom
                >
                  Images
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={isReadOnly}
                >
                  Upload Images
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "fr")}
                    disabled={isReadOnly}
                  />
                </Button>
                {formData.images_fr.length > 0 && (
                  <Box
                    sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {formData.images_fr.map((img, index) => (
                      <Box
                        key={index}
                        sx={{ position: "relative" }}
                      >
                        <img
                          src={img}
                          alt={`Upload ${index + 1}`}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                        <Button
                          size="small"
                          color="error"
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            minWidth: "auto",
                            p: 0.5,
                          }}
                          onClick={() => handleRemoveImage("fr", index)}
                          disabled={isReadOnly}
                        >
                          ✕
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Action Buttons */}
              <Box>
                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={isReadOnly || !formData.title || !formData.text}
                  >
                    Update Message
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </LocalizationProvider>
      </Page>
    </>
  );
};

export default EditMessage;
